/**
 * Manual-play memorial soundtrack — never autoplays by default.
 */
import React, { useRef, useState } from 'react';
import { ExternalLink, Music, Pause, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../../utils/apiBase';
import { isDirectAudioUrl, MUSIC_EXTERNAL_NOTE } from '../../utils/musicLink';

const BACKEND = getBackendBaseUrl();

export function MemorialSoundtrackPlayer({ soundtrack, track: trackProp, featured = false }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);

  const track = trackProp || (soundtrack?.url ? {
    title: soundtrack.title,
    artist: soundtrack.artist,
    link_url: soundtrack.url,
    note: soundtrack.note,
    playback: soundtrack.playback || (isDirectAudioUrl(soundtrack.url) ? 'audio' : 'external'),
    embeddable: soundtrack.embeddable,
    embed_url: soundtrack.embed_url,
  } : null);

  if (!track?.link_url) return null;

  const src = track.link_url.startsWith('http')
    ? track.link_url
    : resolveAssetUrl(BACKEND, track.link_url);

  const playback = track.playback || (isDirectAudioUrl(track.link_url) ? 'audio' : 'external');
  const canEmbed = track.embeddable && track.embed_url && ['youtube', 'spotify', 'soundcloud'].includes(playback);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  return (
    <div className="memorial-public__card p-5 sm:p-6" data-testid="memorial-soundtrack-player">
      {featured ? (
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[hsl(32_10%_42%)]">
          Featured soundtrack
        </p>
      ) : null}
      <h3 className="mt-1 flex items-center gap-2 font-serif text-base text-[hsl(24_14%_18%)]">
        <Music className="h-4 w-4" style={{ color: 'var(--memorial-gold, #c9a961)' }} />
        {track.title || 'Memorial soundtrack'}
      </h3>
      {track.artist ? (
        <p className="mt-0.5 text-sm text-[hsl(32_10%_42%)]">{track.artist}</p>
      ) : (
        <p className="mt-1 text-xs text-[hsl(32_10%_42%)]">Chosen by the family</p>
      )}
      {track.note ? (
        <p className="mt-2 text-sm italic text-[hsl(32_10%_38%)]">{track.note}</p>
      ) : null}
      <p className="mt-2 text-xs text-[hsl(32_10%_46%)]">Tap play when you are ready — music does not start automatically.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {playback === 'audio' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-[var(--memorial-gold,#c9a961)]/40"
            onClick={toggle}
          >
            {playing ? <Pause className="mr-1.5 h-3.5 w-3.5" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
            {playing ? 'Pause' : 'Play memorial soundtrack'}
          </Button>
        ) : canEmbed ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-[var(--memorial-gold,#c9a961)]/40"
            onClick={() => setShowEmbed((v) => !v)}
          >
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {showEmbed ? 'Hide preview' : 'Play memorial soundtrack'}
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" className="rounded-full border-[var(--memorial-gold,#c9a961)]/40" asChild>
            <a href={track.link_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Open song
            </a>
          </Button>
        )}
        {canEmbed || playback === 'audio' ? (
          <Button type="button" size="sm" className="rounded-full bg-[#8B7355] text-white hover:bg-[#7A6548]" asChild>
            <a href={track.link_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Open song
            </a>
          </Button>
        ) : null}
      </div>

      {playback === 'audio' ? (
        <audio
          ref={audioRef}
          src={src}
          preload="none"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
        />
      ) : null}

      {showEmbed && canEmbed ? (
        <div className="mt-4 overflow-hidden rounded-xl border border-[hsl(38_14%_86%)]">
          <iframe
            title={track.title || 'Memorial soundtrack'}
            src={track.embed_url}
            className="aspect-video w-full min-h-[152px]"
            allow="accelerometer; encrypted-media; picture-in-picture"
            loading="lazy"
          />
        </div>
      ) : null}

      <p className="mt-3 text-[11px] text-muted-foreground/80">{MUSIC_EXTERNAL_NOTE}</p>
    </div>
  );
}

export default MemorialSoundtrackPlayer;
